import { ApiError, PaginatedResponse } from '@gorila-shared-ui/components';
import { useEffect } from 'react';
import { RecoilState, SetterOrUpdater, useRecoilState, useRecoilValue } from 'recoil';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { apiParamsState } from '../../storage/app';

export function PaginatedProvider<T>({
  searchState,
  pageState,
  getPaginated,
  setPaginated,
  onSearchChange,
  onUpdateFinish,
  setLoading,
  update,
}: {
  searchState: RecoilState<string | undefined>;
  pageState: RecoilState<number>;
  setLoading: SetterOrUpdater<boolean>;
  getPaginated: (
    clientId: string,
    subClientId: string,
    page: number,
    search?: string
  ) => Promise<{ paginatedResponse?: PaginatedResponse<T>; error?: ApiError }>;
  setPaginated: SetterOrUpdater<PaginatedResponse<T> | undefined>;
  onUpdateFinish: () => void;
  onSearchChange: () => void;
  update?: string;
}) {
  const apiParams = useRecoilValue(apiParamsState);
  const [search, setSearch] = useRecoilState(searchState);
  const [page, setPage] = useRecoilState(pageState);

  const load = async () => {
    const invalid = !apiParams?.clientId || !apiParams?.subClientId;
    if (invalid) return;

    setLoading(true);
    const { paginatedResponse, error } = await getPaginated(apiParams.clientId, apiParams.subClientId, page, search);
    if (!error && paginatedResponse) {
      if (page > 1) {
        setPaginated((prev) => ({
          hasNext: paginatedResponse.hasNext,
          total: paginatedResponse.total,
          items: [...prev!.items, ...paginatedResponse.items],
        }));
      } else {
        setPaginated(paginatedResponse);
      }
    } else {
      setPaginated(undefined);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    return () => {
      setSearch('');
      setPage(1);
    };
  }, [apiParams?.clientId, apiParams?.subClientId, setPage, setSearch]);

  useUpdateEffect(() => {
    setPage(0);
    onSearchChange();
  }, [search]);

  useUpdateEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }

    load();
  }, [page]);

  useUpdateEffect(() => {
    if (!update) return;

    load().finally(() => {
      onUpdateFinish();
    });
  }, [update]);

  return null;
}
